import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
    Card,
    CardHeader,
    CardContent,
    List,
    Box,
    useTheme,
    alpha,
} from '@mui/material';
import {
    SessionQuestionLogEntityFrontend,
    fetchSessionQuestionHistory,
} from '../../../api/sessionQuestionsApi';
import { getLogActionVisuals, getActorFriendlyName, formatLogValue } from './questionFormConstants';

export interface QuestionHistoryModalProps {
    open: boolean;
    questionId: number | null;
    questionLabel: string;
    onClose: () => void;
}

const QuestionHistoryModal: React.FC<QuestionHistoryModalProps> = ({
    open,
    questionId,
    questionLabel,
    onClose,
}) => {
    const theme = useTheme();
    const [logs, setLogs] = useState<SessionQuestionLogEntityFrontend[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !questionId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetchSessionQuestionHistory(questionId)
            .then((data) => {
                if (!cancelled) setLogs(data);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message || 'Failed to load history');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [open, questionId]);

    const handleClose = () => {
        setLogs([]);
        setError(null);
        onClose();
    };

    const preStyle: React.CSSProperties = {
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        padding: '4px 6px',
        borderRadius: '4px',
        fontSize: '0.75rem',
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                Change History: &ldquo;{questionLabel}&rdquo;
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : logs.length > 0 ? (
                    <List dense>
                        {logs.slice().reverse().map((log) => {
                            const actionVisuals = getLogActionVisuals(log.action);
                            const actor = getActorFriendlyName(log.actorType, log.actorId);
                            return (
                                <Card
                                    key={log.id}
                                    sx={{
                                        mb: 1.5,
                                        '&:last-child': { mb: 0 },
                                        bgcolor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.background.paper, 0.6)
                                            : 'grey.50',
                                    }}
                                    variant="outlined"
                                >
                                    <CardHeader
                                        avatar={actionVisuals.icon}
                                        disableTypography
                                        title={
                                            <Typography variant="subtitle2" component="span" sx={{ color: actionVisuals.color }}>
                                                {log.action}
                                            </Typography>
                                        }
                                        subheader={
                                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                                                by {actor.name} - {new Date(log.timestamp).toLocaleString()}
                                            </Typography>
                                        }
                                        sx={{ pb: 0, pt: 1, px: 2 }}
                                    />
                                    <CardContent sx={{ pt: 0.5, px: 2, pb: '12px !important' }}>
                                        {log.oldValue && Object.keys(log.oldValue).length > 0 && (
                                            <Box sx={{ mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 'medium' }}>Old Value:</Typography>
                                                <pre style={{
                                                    ...preStyle,
                                                    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.dark, 0.15) : '#eeeeee',
                                                }}>
                                                    {formatLogValue(log.oldValue)}
                                                </pre>
                                            </Box>
                                        )}
                                        {log.newValue && Object.keys(log.newValue).length > 0 && (
                                            <Box sx={{ mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 'medium' }}>New Value:</Typography>
                                                <pre style={{
                                                    ...preStyle,
                                                    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.success.dark, 0.15) : '#e0e0e0',
                                                }}>
                                                    {formatLogValue(log.newValue)}
                                                </pre>
                                            </Box>
                                        )}
                                        {log.details && (
                                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                Details: {log.details}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </List>
                ) : (
                    <Typography color="text.secondary">No history found for this question.</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuestionHistoryModal;
